import {
	useCallback, useMemo,
} from 'react';
import {
	isItemActiveOnDate,
} from '../../utils/dateUtils';

/**
 * VCL-specific duration calculation hook
 * Centralizes provider duration and total duration logic for Visual Content Loop
 */
export const useVCLDurationCalculation = (loopDate) => {
	// Helper function to format duration in seconds to MM:SS format
	const formatDuration = useCallback((totalSeconds) => {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = String(totalSeconds % 60).padStart(2, '0');
		return `${minutes}:${seconds}`;
	}, []);

	// Helper function to parse duration string (e.g., "2:30" -> 150 seconds)
	const parseDuration = useCallback((durationStr) => {
		if (durationStr === 'Dynamic' || durationStr === '0:00') {
			return 0;
		}

		const [ minutes, seconds, ] = durationStr.split(':').map(Number);
		return (minutes * 60) + seconds;
	}, []);

	// Helper function to calculate provider duration (total duration for all slots)
	const getProviderDuration = useCallback((provider) => {
		// Calculate average duration per slot from the displayed slides
		const validSlides = provider.children.filter((slide) => {
			return slide.scheduled ? isItemActiveOnDate(slide, loopDate) : true;
		});

		if (validSlides.length === 0 && provider.type === 'slide_deck') {
			return '0:00';
		}

		// Calculate total duration of all displayed slides
		let totalSlideDuration = validSlides.reduce((total, slide) => {
			// Skip video files as they don't have fixed durations in this context
			if (slide.kind && slide.kind.startsWith('video/') && !slide.duration) {
				return total;
			}

			// For folders, multiply duration by the number of slots (files) in the folder
			// Empty folders have slots=0 and contribute 0 duration
			if (slide.itemType === 'folder') {
				return total + ((slide.duration || 0) * (slide.slots ?? 1));
			}

			return total + (slide.duration || 0);
		}, 0);

		if (provider.type !== 'slide_deck') {
			if (provider.duration) {
				totalSlideDuration = provider.duration;
			} else {
				let settings = (provider.settings || {})['system'] || {};

				totalSlideDuration = settings.duration || settings.monarch_duration || 15; // 15 is default for ads
			}
		}

		// For slide decks: the displayed slides represent what shows in the slots
		// So total provider duration = sum of all slide durations (already accounting for slot count via slicing)
		// For future folder content: each slot would repeat the same content, so duration = slots * content_duration

		let totalProviderDuration;

		if (provider.type === 'slide_deck') {
			// Slide decks: each slot shows a different slide, so total = sum of slide durations
			totalProviderDuration = totalSlideDuration;
		} else {
			// Future folder/gallery content: each slot shows the same content
			// Since children array is already sliced to match slots, just sum the durations
			// (This handles both single-item folders and multi-item scenarios)
			totalProviderDuration = totalSlideDuration;
		}

		return formatDuration(totalProviderDuration);
	}, [ loopDate, formatDuration, ]);

	// Calculate total slots from active providers
	const getTotalSlots = useCallback((providers) => {
		return providers
			.filter((provider) => {
				return isItemActiveOnDate(provider, loopDate) || provider.type !== 'slide_deck';
			})
			.reduce((acc, provider) => {
				return acc + provider.slots;
			}, 0);
	}, [ loopDate, ]);

	// Calculate total duration from all providers
	const getTotalDuration = useCallback((campaigns, backfillContent) => {
		let totalDuration = 0;
		let backfillCount = 0;

		// 1. Calculate total duration for campaigns and total backfill slots needed
		if (campaigns) {
			campaigns
				.filter((provider) => {
					return isItemActiveOnDate(provider, loopDate) || provider.type !== 'slide_deck';
				})
				.forEach((provider) => {
					const durationStr = getProviderDuration(provider);
					const durationInSeconds = parseDuration(durationStr);

					if (provider.type === 'slide_deck') {
						totalDuration += durationInSeconds;
						// Calculate used slots, accounting for folders that take multiple slots
						const usedSlots = provider.children
							.filter((c) => {
								return !c.isBackfillIndicator;
							})
							.reduce((total, child) => {
								// Folders take up child.slots slots (0 for empty folders), regular slides take 1 slot
								return total + (child.itemType === 'folder' ? (child.slots ?? 1) : 1);
							}, 0);
						backfillCount += Math.max(0, provider.slots - usedSlots);
					} else {
						totalDuration += durationInSeconds * provider.slots;
					}
				});
		}

		// 2. Create a queue of durations from backfill providers
		const backfillDurations = [];
		if (backfillContent) {
			backfillContent.forEach((provider) => {
				if (provider.type === 'slide_deck') {
					provider.children.forEach((slide) => {
						if (!slide.isBackfillIndicator) {
							// For folders, each slot represents one file playing at the folder's duration
							if (slide.itemType === 'folder') {
								// Add duration * slots to account for all files in the folder
								for (let i = 0; i < (slide.slots ?? 1); i++) {
									backfillDurations.push(slide.duration);
								}
							} else {
								backfillDurations.push(slide.duration);
							}
						}
					});
				} else { // dynamic provider
					const durationStr = getProviderDuration(provider);
					const durationInSeconds = parseDuration(durationStr);
					for (let i = 0; i < provider.slots; i++) {
						backfillDurations.push(durationInSeconds);
					}
				}
			});
		}

		// 3. Add durations from backfill queue
		const backfillToAdd = backfillDurations.slice(0, backfillCount);
		totalDuration += backfillToAdd.reduce((sum, duration) => {
			return sum + duration;
		}, 0);

		return totalDuration;
	}, [ loopDate, getProviderDuration, parseDuration, ]);

	// Format total duration as MM:SS string
	const getTotalDurationFormatted = useCallback((campaigns, backfillContent) => {
		const totalSeconds = getTotalDuration(campaigns, backfillContent);
		return formatDuration(totalSeconds);
	}, [ getTotalDuration, formatDuration, ]);

	return {
		getProviderDuration,
		getTotalSlots,
		getTotalDuration,
		getTotalDurationFormatted,
		formatDuration,
		parseDuration,
	};
};